import type { ReportDocument, ReportSelection } from "../../services/report";
import { EMPTY_VALUE, formatUtc } from "./reportFormat";
import { ReportWarningNotes } from "./ReportWarningNotes";

// Section 1 of the sheet (EOS-13 design note): organization, site, project, revision,
// jurisdiction profile, module, study key and run revision, run id, generated at (UTC), and
// which rule picked the run. Every value is the document's as received; an unassigned run
// prints "Unassigned - no project" in place of the project block, as the traceability panel
// does. The module-name report warning is shown here, beside the module it names.

const SELECTION_TEXT: Record<ReportSelection["rule"], string> = {
  RUN_ID: "Run selected by id",
  APPROVED: "Approved run",
  LATEST: "Latest revision - not approved",
};

/** "Approved run · revision 4 exists, not approved" */
export function selectionText(selection: ReportSelection): string {
  const rule = SELECTION_TEXT[selection.rule];
  if (!selection.later_revision_exists) {
    return rule;
  }
  const state = selection.later_revision_approved ? "approved" : "not approved";
  return `${rule} · revision ${selection.latest_revision_number} exists, ${state}`;
}

type ReportHeaderProps = {
  document: ReportDocument;
};

export function ReportHeader({ document }: ReportHeaderProps) {
  const { header, module, selection } = document;
  const assigned = header.project_code !== null;

  return (
    <section aria-label="Report header" data-report-section="header" data-run-id={document.run_id}>
      <h2>Calculation report</h2>
      <dl>
        <dt>Organization</dt>
        <dd data-report-field="organization">{header.organization_name ?? EMPTY_VALUE}</dd>
        {assigned ? (
          <>
            <dt>Site</dt>
            <dd data-report-field="site">
              {header.site_code ?? EMPTY_VALUE} — {header.site_name ?? EMPTY_VALUE}
            </dd>
            <dt>Project</dt>
            <dd data-report-field="project">
              {header.project_code} — {header.project_name ?? EMPTY_VALUE}
            </dd>
            <dt>Client</dt>
            <dd data-report-field="client">{header.client_name ?? EMPTY_VALUE}</dd>
            <dt>Project revision</dt>
            <dd data-report-field="revision">
              {header.revision_label ?? EMPTY_VALUE} (revision{" "}
              {header.revision_number ?? EMPTY_VALUE}) · {header.revision_status ?? EMPTY_VALUE}
            </dd>
          </>
        ) : (
          <>
            <dt>Project</dt>
            <dd data-report-field="project">Unassigned - no project</dd>
          </>
        )}
        <dt>Jurisdiction profile</dt>
        <dd data-report-field="profile">{header.jurisdiction_profile}</dd>
        <dt>Module</dt>
        <dd data-report-field="module">
          {module.module_code} — {module.module_name}
        </dd>
        <dt>Calculation type</dt>
        <dd data-report-field="calculation-type">{module.calculation_type}</dd>
        <dt>Study</dt>
        <dd data-report-field="study">
          {module.calculation_key} · revision {module.revision_number}
        </dd>
        <dt>Run ID</dt>
        <dd data-report-field="run-id">{document.run_id}</dd>
        <dt>Generated</dt>
        <dd data-report-field="generated-at">{formatUtc(document.generated_at)}</dd>
        <dt>Selected by</dt>
        <dd data-report-field="selection" data-report-selection={selection.rule}>
          {selectionText(selection)}
        </dd>
      </dl>
      <ReportWarningNotes document={document} section="header" />
    </section>
  );
}

export default ReportHeader;
